import { useUserContext } from 'contexts/user';
import { usePreferencesContext } from 'contexts/preferences';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import { dasherize } from '@zoocasa/node-kit/strings/dasherize';
import { capitalizeWords } from '@zoocasa/node-kit/strings';
import { CountryCode, CountryCodeList } from 'types/countries';
import { normalizeDescription } from 'data/search-predictions';
import { analyzeLocationPath } from 'utils/location-type-detector';
import { AddressType } from '@zoocasa/go-search';

import type { IUserLocation } from 'utils/user-location';
import type SearchPrediction from 'data/search-predictions';

// Location resolution types
export interface ResolvedLocationData {
  cityName: string;
  citySlug: string;
  siteLocation: CountryCode;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  source: 'recentSearch' | 'userLocation' | 'countryDefault';
  /**
   * Full URL path when the location is a neighbourhood or address.
   * This is used to preserve the full path structure when building navigation URLs.
   */
  fullUrlPath?: string;
  /**
   * Whether this location is a neighbourhood (has more than city-level specificity).
   */
  isNeighbourhood?: boolean;
}

/**
 * TODO: Move this to the core package later.
 * Custom hook for location resolution
 * Resolves location data from various sources with priority order:
 * 1. Recent searches (first priority)
 * 2. User location (second priority) 
 * 3. Country default (fallback)
 */
export function useLocationResolution(): ResolvedLocationData {
  const { recentSearches } = usePreferencesContext();
  const { userLocation, siteLocation } = useUserContext();

  // Location resolution functions
  const resolveLocationFromRecentSearch = (
    recentSearches?: SearchPrediction[]
  ): ResolvedLocationData | null => {
    if (!recentSearches?.length) {
      return null;
    }

    const mostRecent = recentSearches[0];
    if (!mostRecent?.description) {
      return null;
    }

    // Extract clean city name from description
    const parts = mostRecent.description.split(',').map(part => part.trim());
    const cityPart = parts[0];
    
    if (!cityPart) {
      return null;
    }

    const cityName = capitalizeWords(cityPart);
    
    // Use urlPath as primary source, fall back to description
    let citySlug: string;
    let fullUrlPath: string | undefined;
    let isNeighbourhood = false;
    
    if (mostRecent.urlPath) {
      // Analyze the URL path to detect location type and extract city slug
      const { locationType, citySlug: extractedSlug } = analyzeLocationPath(mostRecent.urlPath);
      
      citySlug = extractedSlug;
      isNeighbourhood = locationType === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD;
      
      if (isNeighbourhood) {
        fullUrlPath = mostRecent.urlPath;
      }
    } else {
      // Fall back to description processing
      const normalizedDescription = normalizeDescription(mostRecent.description);
      citySlug = dasherize(normalizedDescription.replaceAll(',', '').toLowerCase());
    }

    return {
      cityName,
      citySlug,
      siteLocation,
      coordinates: {
        latitude: mostRecent.data?.position?.latitude || mostRecent.data?.position?.coordinates?.[1] || 0,
        longitude: mostRecent.data?.position?.longitude || mostRecent.data?.position?.coordinates?.[0] || 0,
      },
      source: 'recentSearch',
      fullUrlPath,
      isNeighbourhood,
    };
  };

  const resolveLocationFromUserLocation = (
    userLocation?: IUserLocation
  ): ResolvedLocationData | null => {    
    if (!userLocation?.name) {
      return null;
    }

    // Extract clean city name, removing any province/state info
    const parts = userLocation.name.split(',').map(part => part.trim());
    const cityPart = parts[0];
    
    if (!cityPart) {
      return null;
    }

    const cityName = capitalizeWords(cityPart);

    return {
      cityName,
      citySlug: userLocation.slug || dasherize(cityName.toLowerCase()),
      siteLocation,
      coordinates: {
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      },
      source: 'userLocation',
    };
  };

  const resolveLocationFromCountryDefault = (): ResolvedLocationData => {
    const defaultLocation = siteLocation === CountryCodeList.UNITED_STATES ? 
      defaultUSCityPayload : 
      defaultCACityPayload;
    
    return {
      cityName: defaultLocation.name,
      citySlug: defaultLocation.slug,
      siteLocation,
      coordinates: {
        latitude: defaultLocation.latitude,
        longitude: defaultLocation.longitude,
      },
      source: 'countryDefault',
    };
  };

  // Main location resolution with priority order
  // Try recent search first
  const fromRecentSearch = resolveLocationFromRecentSearch(recentSearches);
  if (fromRecentSearch) {
    return fromRecentSearch;
  }

  // Try user location second
  const fromUserLocation = resolveLocationFromUserLocation(userLocation);
  if (fromUserLocation) {
    return fromUserLocation;
  }

  // Fallback to country default
  return resolveLocationFromCountryDefault();
}
